import type {
  FollowerModifiers,
  FollowerSkill,
  FollowerTemplate,
  FollowerTemplateUpgrade,
  FollowerTrait,
} from '../schemas';
import type { MoveRef } from '../schemas/character';
import {
  applyPeculiarityEffect,
  type PeculiarityDispatchAcc,
  type PeculiarityDispatchContext,
  type PeculiarityInput,
} from './followerPeculiarities';

/**
 * Risultato della pipeline `applyUpgrades` (D8c — FI-41 esteso).
 *
 * - **`effectiveTemplate`** (D8b): template post-applicazione degli override
 *   statici (`levelOverride`, `hpRuleOverride`, `modifierBonusOverride`,
 *   `primarySkillRankOverride`). Sostituisce il template originale come
 *   base per tutti i mapping successivi in `buildFollowerFromTemplate`.
 *
 * - **`additionalTraits`** (D8c): trait runtime aggiunti dagli upgrade —
 *   sia dai `additionalAlwaysTraitsFromNarrativeFields` (Ben Addestrato,
 *   Alleato Innaturale: 2 capacità custom) sia dai `kind:
 *   'custom-trait-from-narrative'` di `peculiaritiesFromChoiceGroup`
 *   (Servitore Competente opt 2, Maestria opt 1).
 *
 * - **`additionalSkills`** (D8c): skill runtime aggiunte dagli effetti
 *   `kind: 'skill-rank-from-narrative'` di `peculiaritiesFromChoiceGroup`
 *   (Servitore Competente, Maestria del Servitore). `buildFollowerFromTemplate`
 *   le fonde con le skill esistenti via `mergeSkills` (max rank cross-upgrade).
 *
 * - **`additionalModifiers`** (D8c): delta ai modifier dagli effetti
 *   `kind: 'modifier-from-narrative'` (raro per FI-41, ma supportato).
 *   `buildFollowerFromTemplate` li applica come override sui `modifiers`
 *   già calcolati.
 */
export interface UpgradeApplicationResult {
  effectiveTemplate: FollowerTemplate;
  additionalTraits: FollowerTrait[];
  additionalSkills: FollowerSkill[];
  additionalModifiers: Partial<FollowerModifiers>;
}

/**
 * **D8b → D8c**: applica gli upgrade del template (statici + choice-based).
 *
 * **Statici** (D8b): per ogni `upgrade.fromMoveId` presente in `ownedMoveIds`,
 * sostituisce nei campi del template (`baseStats.level/hpRule`,
 * `modifierBonus`, `primarySkillFromNarrativeField.rank`) i valori
 * dell'override. Composizione in ordine di `template.upgrades[]`.
 *
 * **Additivi** (D8c — FI-41): per ogni upgrade applicabile, produce trait /
 * skill / modifier delta da:
 *  - `additionalAlwaysTraitsFromNarrativeFields[]` (capacità sempre presenti
 *    dall'upgrade, label/desc dal `MoveRef` della mossa di upgrade)
 *  - `peculiaritiesFromChoiceGroup` (effetti choice-based del giocatore
 *    sull'upgrade, dispatch via `applyPeculiarityEffect`)
 *
 * Quando `upgradeMoveRefs` è `undefined` o non contiene una mossa applicabile,
 * gli additivi vengono saltati (caso retrocompat D8b: Khan dei Khan non
 * richiede `MoveRef` perché non ha campi D8c).
 *
 * Ritorna sempre un risultato strutturato (mai `null`). Se nessun upgrade
 * è applicabile, `effectiveTemplate` è il template originale (referential
 * equality preservata) e gli array additivi sono vuoti.
 */
export function applyUpgrades(
  template: FollowerTemplate,
  ownedMoveIds: readonly string[],
  upgradeMoveRefs?: ReadonlyMap<string, MoveRef>,
): UpgradeApplicationResult {
  const empty: UpgradeApplicationResult = {
    effectiveTemplate: template,
    additionalTraits: [],
    additionalSkills: [],
    additionalModifiers: {},
  };
  if (template.upgrades.length === 0) return empty;
  const ownedSet = new Set(ownedMoveIds);
  const applicableUpgrades = template.upgrades.filter((u) => ownedSet.has(u.fromMoveId));
  if (applicableUpgrades.length === 0) return empty;

  let effectiveTemplate = template;
  const additionalTraits: FollowerTrait[] = [];
  const additionalSkills: FollowerSkill[] = [];
  const additionalModifiers: Partial<FollowerModifiers> = {};

  for (const upgrade of applicableUpgrades) {
    effectiveTemplate = applyStaticOverrides(effectiveTemplate, upgrade);
    const moveRef = upgradeMoveRefs?.get(upgrade.fromMoveId);
    if (moveRef === undefined) continue;
    additionalTraits.push(...applyAdditionalAlwaysTraits(upgrade, moveRef, template.id));
    const choiceDelta = applyUpgradeChoicePeculiarities(template, upgrade, moveRef);
    additionalTraits.push(...choiceDelta.traitAdditions);
    additionalSkills.push(...choiceDelta.skillAdditions);
    mergeModifierDelta(additionalModifiers, choiceDelta.modifierDelta);
  }

  return { effectiveTemplate, additionalTraits, additionalSkills, additionalModifiers };
}

// ---------------------------------------------------------------------------
// Static overrides (D8b + D8c primarySkillRankOverride)
// ---------------------------------------------------------------------------

function applyStaticOverrides(
  current: FollowerTemplate,
  upgrade: FollowerTemplateUpgrade,
): FollowerTemplate {
  const next: FollowerTemplate = {
    ...current,
    baseStats: {
      ...current.baseStats,
      level: upgrade.levelOverride ?? current.baseStats.level,
      hpRule: upgrade.hpRuleOverride ?? current.baseStats.hpRule,
    },
    modifierBonus: upgrade.modifierBonusOverride ?? current.modifierBonus,
  };
  if (upgrade.primarySkillRankOverride !== undefined && current.primarySkillFromNarrativeField) {
    next.primarySkillFromNarrativeField = {
      ...current.primarySkillFromNarrativeField,
      rank: upgrade.primarySkillRankOverride,
    };
  }
  return next;
}

// ---------------------------------------------------------------------------
// D8c — additivi: trait sempre presenti dall'upgrade
// ---------------------------------------------------------------------------

function applyAdditionalAlwaysTraits(
  upgrade: FollowerTemplateUpgrade,
  moveRef: MoveRef,
  templateId: string,
): FollowerTrait[] {
  if (upgrade.additionalAlwaysTraitsFromNarrativeFields.length === 0) return [];
  const fields = moveRef.narrativeFieldValues ?? {};
  return upgrade.additionalAlwaysTraitsFromNarrativeFields.map((config) => {
    const label = fields[config.labelFieldId]?.trim() ?? '';
    if (label.length === 0) {
      throw new Error(
        `Template '${templateId}' upgrade '${upgrade.fromMoveId}': additionalAlwaysTraitsFromNarrativeFields richiede NarrativeField '${config.labelFieldId}' compilato (label)`,
      );
    }
    const description = config.descriptionFieldId
      ? (fields[config.descriptionFieldId]?.trim() ?? '')
      : '';
    return {
      id: config.traitId,
      label,
      ...(description.length > 0 ? { description } : {}),
    };
  });
}

// ---------------------------------------------------------------------------
// D8c — additivi: peculiarities choice-based dall'upgrade
// ---------------------------------------------------------------------------

interface ChoiceDelta {
  traitAdditions: FollowerTrait[];
  skillAdditions: FollowerSkill[];
  modifierDelta: Partial<FollowerModifiers>;
}

function applyUpgradeChoicePeculiarities(
  template: FollowerTemplate,
  upgrade: FollowerTemplateUpgrade,
  moveRef: MoveRef,
): ChoiceDelta {
  const mapping = upgrade.peculiaritiesFromChoiceGroup;
  if (!mapping) return { traitAdditions: [], skillAdditions: [], modifierDelta: {} };
  const selected = moveRef.selectedChoiceGroups?.[mapping.fromChoiceGroup] ?? [];
  if (selected.length === 0) return { traitAdditions: [], skillAdditions: [], modifierDelta: {} };

  // Per gli effetti dell'upgrade choice-based, l'utente non può duplicare la
  // selezione (le opzioni sono single-select sull'upgrade — non è "allow
  // duplicate"). Quindi `count` è sempre 1 per opzione.
  const baseModifiers: FollowerModifiers = { ...template.baseStats.modifiers };
  const acc: PeculiarityDispatchAcc = {
    modifiers: baseModifiers,
    skillAdditions: [],
    traitAdditions: [],
    costSuspended: false,
  };
  const input: PeculiarityInput = {
    selectedChoiceGroups: moveRef.selectedChoiceGroups ?? {},
    narrativeFieldValues: moveRef.narrativeFieldValues ?? {},
  };

  for (const optionId of selected) {
    const effects = mapping.optionToEffect[optionId];
    if (!effects) {
      throw new Error(
        `Template '${template.id}' upgrade '${upgrade.fromMoveId}': nessun effetto mappato per opzione '${optionId}'`,
      );
    }
    const ctx: PeculiarityDispatchContext = {
      template,
      input,
      optionId,
      count: 1,
    };
    for (const effect of effects) {
      applyPeculiarityEffect(effect, ctx, acc);
    }
  }

  const modifierDelta = diffModifiers(template.baseStats.modifiers, acc.modifiers);
  return {
    traitAdditions: acc.traitAdditions,
    skillAdditions: acc.skillAdditions,
    modifierDelta,
  };
}

function diffModifiers(
  before: FollowerModifiers,
  after: FollowerModifiers,
): Partial<FollowerModifiers> {
  const delta: Partial<FollowerModifiers> = {};
  for (const key of Object.keys(after) as (keyof FollowerModifiers)[]) {
    if (after[key] !== before[key]) delta[key] = after[key];
  }
  return delta;
}

function mergeModifierDelta(
  acc: Partial<FollowerModifiers>,
  next: Partial<FollowerModifiers>,
): void {
  for (const key of Object.keys(next) as (keyof FollowerModifiers)[]) {
    const value = next[key];
    if (value !== undefined) acc[key] = value;
  }
}
